import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { initDB, loginUser, logoutUser, fetchPerfil, supabase } from '../db/engine';
import { setMoneda } from '../utils/moneda';
import { aplicarMarca, getMarca, cargarMarcaLocal } from '../utils/marca';

const AppContext = createContext(null);

// Arma el objeto de sesión que usa la app a partir de la ficha de `usuarios`.
function armarSesion(u) {
  const role = u.roles || {};
  return {
    id: u.id,
    nombre: u.nombre,
    email: u.email,
    rol_id: u.rol_id,
    rol_nombre: role.nombre,
    es_master: role.es_master,
    ver_beneficiarios: role.ver_beneficiarios || false,
    ampliar_cupo_ia: role.ampliar_cupo_ia || false,
    es_soporte: !!u.es_soporte,
    agente_id: u.agente_id || null,
    ejecutivo_id: u.ejecutivo_id || null,
    orden_modulos: u.orden_modulos || null,
    debe_cambiar_password: !!u.debe_cambiar_password,
    tour_completado: !!u.tour_completado,
    permisos: role.permisos || {},
  };
}

// LICENCIA (Corredata): `cont_config.licencia_vence` es el último día con licencia; la
// escribe solo el portal (fin de la prueba, o suspensión) y el candado de cont_config
// impide moverla desde el navegador. Al día siguiente empiezan los días de GRACIA: se
// trabaja normal con el aviso a la vista; cumplidos, se cierra el acceso (los datos no
// se tocan). NULL = sin vencimiento (Riesgo Unido, clientes al día).
export const DIAS_GRACIA = 5;

// Inactividad: minutos sin usar el sistema antes de cerrar la sesión (ver el efecto en AppProvider).
const MIN_INACTIVIDAD = 30;
const CLAVE_ACTIVIDAD = 'ultima_actividad';
const marcarActividad = () => { try { localStorage.setItem(CLAVE_ACTIVIDAD, String(Date.now())); } catch { /* sin almacenamiento */ } };
const ultimaActividad = () => { try { return Number(localStorage.getItem(CLAVE_ACTIVIDAD)) || 0; } catch { return 0; } };
function estadoLicencia(vence) {
  if (!vence || !/^\d{4}-\d{2}-\d{2}/.test(String(vence))) return null;
  const hoy = new Date(Date.now() - 5 * 3600 * 1000).toISOString().slice(0, 10);   // fecha de Panamá
  const dia = (s) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10)) / 86400000;
  const pasados = dia(hoy) - dia(String(vence).slice(0, 10));                         // 1 = el día 91
  if (pasados <= 0) return null;
  const restantes = DIAS_GRACIA + 1 - pasados;                                         // día 91 → 5
  const [y, m, d] = String(vence).slice(0, 10).split('-');
  return { vence: `${d}/${m}/${y}`, restantes: Math.max(0, restantes), bloqueada: restantes <= 0 };
}

export function AppProvider({ children }) {
  const [dbReady, setDbReady] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Alcance del agente: si el usuario está vinculado a un agente (y no es master),
  // solo puede ver SUS pólizas/clientes. null = sin restricción (ve todo).
  const [agenteScope, setAgenteScope] = useState(null);
  // Marca del sistema (white-label). Arranca con la última marca conocida (caché
  // local, para brandear el login antes de la sesión) y se sobrescribe con la de
  // `cont_config` al iniciar sesión. Ver `src/utils/marca.js`.
  const [marca, setMarca] = useState(() => cargarMarcaLocal());
  // Módulos que incluye el plan contratado (Corredata). null = todos (sin plan).
  const [modulosPlan, setModulosPlan] = useState(null);
  const [planNombre, setPlanNombre]   = useState(null);
  const [licencia, setLicencia]       = useState(null);   // null = sin vencimiento
  // Guía interactiva de primer ingreso (onboarding). Se abre sola la primera vez
  // que entra un master que no la ha completado; se puede reabrir a mano.
  const [tourActivo, setTourActivo] = useState(false);
  const tourAutoRef = useRef(false);   // para no reabrirla en cada cambio de `user`

  // El usuario de SOPORTE (soporte@corredata) solo puede estar dentro mientras el
  // cliente tenga abierta la ventana de 24h (cont_config.soporte_hasta en el futuro).
  // Aunque el baneo de Auth no se haya re-aplicado, este chequeo lo saca al expirar.
  const accesoSoporteVigente = async () => {
    const { data } = await supabase.from('cont_config').select('soporte_hasta').eq('id', 1).maybeSingle();
    const hasta = data?.soporte_hasta ? new Date(data.soporte_hasta).getTime() : 0;
    return hasta > Date.now();
  };

  // Restaurar sesión al cargar. La verdad la tiene el token firmado que guarda
  // Supabase, NO un id en localStorage: antes bastaba con escribir {"id":1} en el
  // navegador para entrar como master. Con `getSession` eso ya no es posible.
  useEffect(() => {
    let cancel = false;
    initDB().then(async () => {
      if (cancel) return;
      setDbReady(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const perfil = await fetchPerfil(session.user.id);
        // Ficha inactiva, o sesión de soporte cuya ventana ya venció → fuera.
        if (perfil && (!perfil.es_soporte || await accesoSoporteVigente())) setUser(armarSesion(perfil));
        else await logoutUser();
      }
      if (!cancel) setLoading(false);
    }).catch(() => {
      if (cancel) return;
      setError('No se pudo conectar a la base de datos. Verifica las variables de entorno.');
      setLoading(false);
    });

    // Si el token expira o se cierra sesión en otra pestaña, salir aquí también.
    const { data: sub } = supabase.auth.onAuthStateChange((evento) => {
      if (evento === 'SIGNED_OUT') { setUser(null); setAgenteScope(null); }
    });
    return () => { cancel = true; sub?.subscription?.unsubscribe(); };
  }, []);

  // La configuración del sistema (moneda + marca) vive en `cont_config`, que solo
  // se puede leer con sesión iniciada. Un `select('*')` tolera que las columnas de
  // marca aún no existan (pre-migración): trae lo que haya y cae a los defaults.
  const cargarConfig = async () => {
    const { data } = await supabase.from('cont_config').select('*').eq('id', 1).maybeSingle();
    if (data?.moneda) setMoneda(data.moneda);
    setMarca(aplicarMarca(data || {}));
    // Módulos contratados en el plan de esta instancia (Corredata). null/vacío =
    // TODOS activos, que es como se queda una instancia sin plan configurado
    // (ej. Riesgo Unido): así el gating no cambia nada donde no se ha vendido.
    const mods = data?.modulos_activos;
    setModulosPlan(Array.isArray(mods) && mods.length ? mods : null);
    setPlanNombre(data?.plan || null);
    setLicencia(estadoLicencia(data?.licencia_vence));
  };
  useEffect(() => {
    if (!user) return;
    cargarConfig().catch(() => { /* usa moneda y marca por defecto */ });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    const u = await loginUser(email, password);
    if (!u) return false;
    // Un usuario de soporte con la ventana cerrada no debe quedar dentro.
    if (u.es_soporte && !(await accesoSoporteVigente())) { await logoutUser(); return false; }
    marcarActividad();   // si no, la última actividad de la sesión anterior la cerraría al entrar
    setUser(armarSesion(u));
    return true;
  };

  const logout = async () => {
    await logoutUser();
    setUser(null);
    setAgenteScope(null);
  };

  // CIERRE POR INACTIVIDAD (seguridad, pedido por Ricardo 2026-10-03): sin actividad en
  // MIN_INACTIVIDAD minutos se cierra la sesión; para volver basta usuario y contraseña.
  // La última actividad vive en localStorage para que TODAS las pestañas cuenten juntas
  // (trabajar en una no cierra la otra) y para que reabrir el navegador horas después
  // encuentre la sesión ya vencida. Respaldo en el servidor: Supabase → Auth → Sessions.
  useEffect(() => {
    if (!user) return;
    let marcado = 0;
    const alActuar = () => {
      const ahora = Date.now();
      if (ahora - marcado > 15000) { marcado = ahora; marcarActividad(); }   // máximo cada 15 s
    };
    const revisar = async () => {
      const ult = ultimaActividad();
      if (ult && Date.now() - ult > MIN_INACTIVIDAD * 60000) {
        try { sessionStorage.setItem('cierre_inactividad', '1'); } catch { /* sin almacenamiento */ }
        await logout();
      }
    };
    if (!ultimaActividad()) marcarActividad();
    revisar();
    const eventos = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    eventos.forEach(e => window.addEventListener(e, alActuar, { passive: true }));
    document.addEventListener('visibilitychange', revisar);
    const intervalo = setInterval(revisar, 60000);
    return () => {
      eventos.forEach(e => window.removeEventListener(e, alActuar));
      document.removeEventListener('visibilitychange', revisar);
      clearInterval(intervalo);
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recarga la ficha del usuario ya autenticado (tras el cambio obligatorio de
  // contraseña, para que se apague la pantalla de "debe cambiar" y entre).
  const refrescarPerfil = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const perfil = await fetchPerfil(session.user.id);
    if (perfil) setUser(armarSesion(perfil));
  };

  // Carga el alcance del agente (sus pólizas y clientes) cuando el usuario está
  // vinculado a un agente y NO es master. Si no aplica, queda null (ve todo).
  useEffect(() => {
    let cancel = false;
    if (!user || user.es_master || !user.agente_id) { setAgenteScope(null); return; }
    (async () => {
      try {
        const { data: pa } = await supabase.from('poliza_agentes')
          .select('poliza_id').eq('agente_id', user.agente_id);
        const polizaIds = [...new Set((pa || []).map(x => x.poliza_id).filter(v => v != null))];
        let clienteIds = [], reclamoIds = [];
        if (polizaIds.length) {
          const { data: pols } = await supabase.from('polizas').select('cliente_id').in('id', polizaIds);
          clienteIds = [...new Set((pols || []).map(p => p.cliente_id).filter(v => v != null))];
          const { data: recs } = await supabase.from('reclamos').select('id').in('poliza_id', polizaIds);
          reclamoIds = [...new Set((recs || []).map(r => r.id).filter(v => v != null))];
        }
        if (!cancel) setAgenteScope({ agenteId: user.agente_id, polizaIds, clienteIds, reclamoIds });
      } catch {
        if (!cancel) setAgenteScope({ agenteId: user.agente_id, polizaIds: [], clienteIds: [], reclamoIds: [] });
      }
    })();
    return () => { cancel = true; };
  }, [user]);

  // ── Gating de módulos por PLAN (Corredata) ────────────────────────────────
  // Qué módulos incluye el plan contratado por esta instancia. Es una capa POR
  // ENCIMA de los permisos de rol: si el módulo no está en el plan, no lo ve
  // NADIE (ni el master). `modulosPlan === null` = sin plan configurado = todo
  // activo (Riesgo Unido y cualquier instancia previa al gating).
  // Dashboard y Administración NUNCA se gatean: sin ellos el cliente no podría
  // ni entrar ni configurar su sistema (se quedaría encerrado).
  const MODULOS_SIEMPRE = ['dashboard', 'admin'];
  const moduloEnPlan = (modulo) => {
    if (!modulo) return true;
    if (!modulosPlan) return true;                       // sin plan = todo activo
    if (MODULOS_SIEMPRE.includes(modulo)) return true;
    return modulosPlan.includes(modulo);
  };

  const canView = (modulo) => {
    if (!user) return false;
    if (!moduloEnPlan(modulo)) return false;             // el plan manda sobre el rol
    if (user.es_master) return true;
    return user.permisos?.[modulo]?.ver === true;
  };

  const canEdit = (modulo) => {
    if (!user) return false;
    if (!moduloEnPlan(modulo)) return false;
    if (user.es_master) return true;
    return user.permisos?.[modulo]?.editar === true;
  };

  // ¿El usuario está restringido a su propia cartera de agente?
  const esAgenteRestringido = !!(user && !user.es_master && user.agente_id);

  // ¿El usuario es un ejecutivo (vinculado a una ficha de ejecutivo y no master)?
  // En el CRM ve por defecto solo sus tareas, pero conserva el botón "Todos".
  const esEjecutivo = !!(user && !user.es_master && user.ejecutivo_id);

  // ¿Puede VER el detalle de los beneficiarios (información confidencial)?
  // El master siempre; los demás roles solo si el master les dio el permiso
  // `ver_beneficiarios`. Los que no pueden solo ven la FECHA de última actualización.
  const puedeVerBeneficiarios = !!(user && (user.es_master || user.ver_beneficiarios));

  // Guarda el orden personalizado de los módulos del sidebar para este usuario.
  const guardarOrdenModulos = async (orden) => {
    if (!user) return;
    const json = JSON.stringify(orden || []);
    await supabase.from('usuarios').update({ orden_modulos: json }).eq('id', user.id);
    setUser(prev => ({ ...prev, orden_modulos: json }));
  };

  // Abre la guía de inicio sola: solo master, solo si no la ha completado, y una
  // vez por sesión (el ref evita que reabra al refrescarse `user` por otra razón).
  useEffect(() => {
    if (user && user.es_master && !user.tour_completado && !tourAutoRef.current) {
      tourAutoRef.current = true;
      setTourActivo(true);
    }
  }, [user]);

  const iniciarTour = () => setTourActivo(true);
  // Cierra la guía. Si se completó/omitió, marca `tour_completado` para no repetir.
  const cerrarTour = async (completado = true) => {
    setTourActivo(false);
    if (completado && user) {
      try { await supabase.from('usuarios').update({ tour_completado: true }).eq('id', user.id); } catch { /* no bloquear */ }
      setUser(prev => (prev ? { ...prev, tour_completado: true } : prev));
    }
  };

  return (
    <AppContext.Provider value={{ dbReady, user, loading, error, login, logout, refrescarPerfil, canView, canEdit, moduloEnPlan, planNombre, licencia, agenteScope, esAgenteRestringido, esEjecutivo, puedeVerBeneficiarios, guardarOrdenModulos, marca, refrescarMarca: () => cargarConfig().catch(() => {}), tourActivo, iniciarTour, cerrarTour }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
